// @flow

import * as React from 'react';
import cx from 'classnames';

import FontAwesomeIcon, {
  lightAngleRight
} from 'common/components/FontAwesomeIcon';

import styles from './PreviewImage.module.scss';

import type { PreviewImageType } from './PreviewImages';

type Props = { image?: PreviewImageType, ... };

type ButtonProps = { handleClick: () => void, ... };

function defaultPreventer(callback: () => void): MouseEvent => void {
  return e => {
    e.preventDefault();
    callback();
  };
}

export function NextButton(props: ButtonProps): React.Node {
  return (
    <button
      className={styles.nextButton}
      onClick={defaultPreventer(props.handleClick)}
      aria-label={__('next')}
    >
      <FontAwesomeIcon icon={lightAngleRight} />
    </button>
  );
}

export function PreviousButton(props: ButtonProps): React.Node {
  return (
    <button
      className={styles.prevButton}
      onClick={defaultPreventer(props.handleClick)}
      aria-label={__('previous')}
    >
      <FontAwesomeIcon icon={lightAngleRight} />
    </button>
  );
}

export default function PreviewImage(props: Props): React.Node {
  const [loading, setLoading] = React.useState(true);
  const imageNode = React.useRef<HTMLImageElement | null>(null);
  const loadingTimeoutRef = React.useRef<TimeoutID | null>(null);
  const { image } = props;

  const imageLoaded = React.useCallback(() => {
    clearTimeout(loadingTimeoutRef.current);
    setLoading(false);
  }, []);

  React.useEffect(() => {
    // Reset loading state when image changes
    clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = setTimeout(() => {
      setLoading(true);
    }, 500);

    // Check if image is already loaded on mount
    if (imageNode.current && imageNode.current.complete) {
      imageLoaded();
    }

    return () => {
      clearTimeout(loadingTimeoutRef.current);
    };
  }, [image, imageLoaded]);

  const { url: imageUrl, placeholder } = image || {};
  const renderImage = !!imageUrl;
  const backgroundImage = placeholder ? `url('${placeholder}')` : null;

  return (
    <div
      className={styles.previewImageWrapper}
      style={{
        backgroundImage: backgroundImage
      }}
    >
      {renderImage && (
        <img
          data-testid="venue-image"
          src={imageUrl}
          className={cx({
            [styles['previewImage']]: true,
            [styles['previewImage--loading']]: loading
          })}
          height="400"
          width="600"
          ref={imageNode}
          onLoad={imageLoaded}
        />
      )}
    </div>
  );
}
